import { FC } from 'react'
import _ from 'lodash'
import dayjs from 'dayjs'
import { getNominative } from '../../../common/utils'
import { ResidentListItem, Spinner } from '../../../components/generic'
import { useSelector } from 'react-redux'
import { Carer, Home, ResidentItem } from '@lmc/ts-common'
import { CardTitle, EmptyItems } from './CardComponents'

const TITLE = 'Birthdays'

type Props = {
  residents: ResidentItem[]
  carers: Carer[]
  isLoading: boolean
}

type ResidentExtended = ResidentItem & {
  active: boolean
  nextBirthday: number
  type: string
}

// TODO find out what is this type
type Birthday = any

export const BirthdaysCard: FC<Props> = ({ residents, carers, isLoading }) => {
  const preferredPeopleNominative: string = useSelector(
    (state: { global: { home: Home } }) =>
      state.global.home.preferredPeopleNominative || ''
  )

  const getBirthdays = (residents: ResidentExtended[], carers = []) =>
    _.chain([...residents, ...carers])
      .filter((d) => d.active || d.status === 'active')
      .cloneDeep()
      .forEach((row) => {
        const { age, nextBirthday } = daysUntil(row?.dateOfBirth)
        row.age = age
        row.nextBirthday = nextBirthday
        row.type = row.active ? 'carer' : 'resident'
      })
      .sortBy('nextBirthday')
      .filter(({ nextBirthday }) => nextBirthday < 120)
      .value()

  const daysUntil = (date?: Date | null | string) => {
    const birthday = dayjs(date)
    const today = dayjs().format('YYYY-MM-DD')
    const age = dayjs(today).diff(birthday, 'years')
    const nextBirthday = dayjs(birthday).add(age, 'years')

    if (nextBirthday.isSame(today)) {
      return {
        nextBirthday: nextBirthday.diff(today, 'days'),
        age,
      }
    }

    return {
      nextBirthday: dayjs(birthday)
        .add(age + 1, 'years')
        .diff(today, 'days'),
      age: age + 1,
    }
  }

  const renderBirthdays = (birthdays: Birthday[]) => {
    if (!birthdays || !birthdays.length)
      return <EmptyItems size="lg" items={2} />
    const data = birthdays.map(
      ({ id, type, name, picture, nextBirthday, age }) => {
        let reminderText
        if (nextBirthday === 0) {
          reminderText = 'today!'
        } else if (nextBirthday === 1) {
          reminderText = 'tomorrow'
        } else if (nextBirthday < 6) {
          reminderText = `this coming ${dayjs()
            .add(nextBirthday, 'days')
            .format('dddd')}`
        } else {
          reminderText = `on ${dayjs()
            .add(nextBirthday, 'days')
            .format('dddd Do MMM')}`
        }

        return {
          name: `${age} years old ${reminderText}`,
          subheading: `${name.first} ${name.last}`,
          type,
          id,
          picture,
        }
      }
    )

    return (
      <div style={{ maxHeight: 238, overflowY: 'auto' }}>
        {data.map((row) => (
          <ResidentListItem
            key={row.id}
            data={row}
            link={
              row.type === 'carer'
                ? `/organisation/team/${row.id}/profile`
                : `/residents/${row.id}/profile`
            }
            compact={undefined}
            isActive={undefined}
          />
        ))}
      </div>
    )
  }

  const renderBody = () => {
    if (isLoading) return <Spinner style={{ paddingTop: 40 }} />
    const birthdays = getBirthdays(
      residents as ResidentExtended[],
      carers as never[]
    )
    if (carers && residents && (residents.length || carers.length)) {
      return renderBirthdays(birthdays)
    }
    return `Birthdays will appear once you add a ${getNominative(
      preferredPeopleNominative,
      'resident'
    )} to your home.`
  }

  return (
    <div>
      <div className="lmc-home-card">
        <CardTitle title={TITLE} />
        <div className="lmc-home-card-body-ss">{renderBody()}</div>
      </div>
    </div>
  )
}

export default BirthdaysCard
